import { IconFa } from '@/components/wm';
import { cn } from '@/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useState } from 'react';

import { API_DATE_FORMAT, stateLabels } from '../utils';
import { useFittedHeadline } from './useValueFontSize';

const paletteVariants = {
    primary: 'tw-bg-primary tw-text-secondary',
    secondary: 'tw-bg-secondary tw-text-gray-200',
    simple: 'tw-bg-white tw-border tw-border-solid tw-border-border tw-text-secondary'
};

const iconVariants = {
    primary: 'tw-text-secondary',
    secondary: 'tw-text-gray-200',
    simple: 'tw-text-secondary'
};

const
    BUCKET = 900,
    LOOKBACK_DAYS = 7,
    TIMELINE_SECONDS = 86400;

// A bucket in which the signal changed holds an average, e.g. 0.4.
const toBinary = value => (value >= 0.5 ? 1 : 0);

function currentRun(points) {
    const last = points[points.length - 1];
    let start = points.length - 1;

    while (start > 0 && toBinary(points[start - 1].v) === toBinary(last.v)) {
        start--;
    }

    return {
        lastTimestamp: last.t,
        since: start === 0 ? null : points[start].t,
        value: toBinary(last.v)
    };
}

// Points are placed by the span they cover, not matched to quarter hours: the
// API buckets from the `from` it is given.
function timelineSegments(points, now) {
    const
        from = now - TIMELINE_SECONDS,
        segments = [];

    let covered = from;

    const extend = (state, seconds) => {
        const last = segments[segments.length - 1];

        if (seconds <= 0) {
            return;
        }

        if (last?.state === state) {
            last.seconds += seconds;
        } else {
            segments.push({ seconds, state });
        }
    };

    points.forEach(point => {
        const
            start = Math.max(point.t, covered),
            end = Math.min(point.t + BUCKET, now);

        if (end <= start) {
            return;
        }

        extend('none', start - covered);
        extend(toBinary(point.v), end - start);
        covered = end;
    });

    extend('none', now - covered);

    return segments.map(segment => ({ share: segment.seconds / TIMELINE_SECONDS, state: segment.state }));
}

// What 1 and 0 mean depends on the wiring, and the measurements' `inverted`
// setting is never used, so each value gets its own label and `normalValue`
// says which one is green.
//
// `sample`, when given, stands in for the indicator and its data: the type picker
// shows each widget with made-up values.
export default function StateWidget({ sample, widget }) {
    const
        [fetched, setResult] = useState(null),
        result = sample?.result ?? fetched;

    const
        model = WiseMetering.indicators.get(widget.data.indicatorId),
        palette = widget.graphical.palette || 'primary';

    const
        icon = WiseMetering.icons.getIconFromKind(model?.get('kind_slug')) || 'info',
        normalValue = widget.data.normalValue ?? 1,
        labels = stateLabels(widget.data),
        colorFor = state => (state === 'none'
            ? 'transparent'
            : WiseMetering.SemaphorColors[state === normalValue ? 'green' : 'red']);

    useEffect(() => {
        if (sample || !model) {
            return;
        }

        let cancelled = false;

        setResult(null);

        const fetchData = () => {
            const
                timezone = model.timezone(),
                to = moment.tz(timezone),
                from = to.clone().subtract(LOOKBACK_DAYS, 'days');

            model.getRealtimeData({
                from: from.format(API_DATE_FORMAT),
                rate: BUCKET,
                to: to.format(API_DATE_FORMAT)
            }).then(response => {
                if (cancelled) {
                    return;
                }

                const points = (Array.isArray(response?.data) ? response.data : []).filter(point => point.v !== null);

                setResult(points.length === 0
                    ? { empty: true }
                    : { ...currentRun(points), segments: timelineSegments(points, to.unix()) });
            }).catch(() => {
                if (!cancelled) {
                    setResult({ empty: true });
                }
            });
        };

        fetchData();

        const refresh = setInterval(fetchData, 60000);

        return () => {
            cancelled = true;
            clearInterval(refresh);
        };
    }, [model]);

    const
        isSmallWidget = widget.graphical.size.h <= 3 || widget.graphical.size.w <= 2,
        hasState = Boolean((model || sample) && result && !result.empty),
        headline = hasState ? labels[result.value] : '',
        // "MM " holds the room the icon and its gap take on the headline's line.
        [valueRef, fontSize] = useFittedHeadline(headline && `MM ${headline}`);

    let body;

    if (!hasState) {
        body = (
            <div className="tw-text-sm tw-text-right">
                {!model || result?.empty ? i18next.t('ui.no_data_available') : i18next.t('modal.title.loading')}
            </div>
        );
    } else {
        const
            timezone = model?.timezone() || moment.tz.guess(),
            now = moment(),
            since = result.since !== null &&
                `${i18next.t('common.since').capitalize()} ${moment.unix(result.since).tz(timezone).calendar()} (${moment.duration(now.unix() - result.since, 'seconds').humanize()})`;

        body = (
            <>
                {/* The state's icon on the left, on the headline's line and at its
                    size. The switch's position, not just its colour, tells the
                    states apart. */}
                <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-items-end tw-justify-end" ref={valueRef}>
                    <div
                        className="tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-[0.5em] tw-font-bold tw-leading-tight"
                        style={{ fontSize: `${fontSize}px` }}
                    >
                        <FontAwesomeIcon
                            className="tw-flex-shrink-0"
                            icon={result.value === 1 ? 'toggle-on' : 'toggle-off'}
                            style={{ color: colorFor(result.value) }}
                        />
                        <span className="tw-min-w-0 tw-truncate">{headline}</span>
                    </div>
                    {since && (
                        <div className={cn('tw-font-medium tw-text-right tw-mt-0.5 tw-max-w-full tw-truncate', isSmallWidget ? 'tw-text-sm' : 'tw-text-base')}>
                            {since}
                        </div>
                    )}
                    <div className="tw-text-[11px] tw-text-right tw-opacity-70 tw-mt-[-4px]">
                        {moment.unix(result.lastTimestamp).tz(timezone).format('YYYY/MM/DD HH:mm')}
                    </div>
                </div>
                <div className="tw-mt-1 tw-text-[11px] tw-opacity-70">{i18next.t('dashboard.custom_dashboard.periods.last_24h')}</div>
                <div className="tw-flex tw-h-2 tw-w-full tw-flex-shrink-0 tw-overflow-hidden tw-rounded-sm tw-bg-black/10">
                    {result.segments.map((segment, index) => (
                        <div
                            key={index}
                            style={{ backgroundColor: colorFor(segment.state), width: `${segment.share * 100}%` }}
                        />
                    ))}
                </div>
            </>
        );
    }

    return (
        <div
            className={cn(
                paletteVariants[palette],
                'tw-flex tw-flex-col tw-gap-1 tw-rounded-sm tw-px-4 tw-pt-4',
                'tw-h-full tw-w-full tw-overflow-hidden'
            )}
            id={widget.id}
        >
            <div>
                <div className="tw-flex tw-items-center tw-justify-between">
                    <div className="tw-text-base tw-font-medium tw-leading-none">{widget.title}</div>
                    <IconFa color={iconVariants[palette]} icon={icon} size="medium"/>
                </div>
                <div className="tw-text-md tw-mt-0">
                    {widget.subtitle || ''}
                </div>
            </div>
            <div className="tw-flex tw-flex-1 tw-min-h-0 tw-flex-col tw-justify-end tw-pb-3">
                {body}
            </div>
        </div>
    );
}
